import { useState, useMemo } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Searchbar,
  Sheet,
} from 'konsta/react'
import {
  FileText,
  Clock,
  DoorOpen,
  DollarSign,
  Calendar,
  Users,
  Trophy,
  Spade,
} from 'lucide-react'
import { formatCurrency, formatRelativeTime, cn } from '@/lib/utils'

// --- Types ---

interface HandRecord {
  id: string
  room: string
  players: string[]
  pot: number
  winner: string
  handType: string
  date: Date
  board: string[]
}

interface TimelineAction {
  stage: string
  board?: string[]
  actions: string[]
}

// --- Mock Data ---

const mockHands: HandRecord[] = [
  {
    id: 'H001',
    room: '新手桌 #1',
    players: ['王大明', 'David C.', 'Sarah L.'],
    pot: 904000,
    winner: '王大明',
    handType: '同花順',
    date: new Date(Date.now() - 1000 * 60 * 30),
    board: ['Q♠', 'J♥', '10♦', '8♣', '2♠'],
  },
  {
    id: 'H002',
    room: '中級桌 #3',
    players: ['David C.', 'Mike W.'],
    pot: 400000,
    winner: 'Mike W.',
    handType: '兩對',
    date: new Date(Date.now() - 1000 * 60 * 60),
    board: ['A♣', '7♠', '2♦', '9♥', '4♣'],
  },
  {
    id: 'H003',
    room: '高額桌 VIP',
    players: ['王大明', '小美', 'Alex K.', 'Sarah L.'],
    pot: 2400000,
    winner: '王大明',
    handType: '皇家同花順',
    date: new Date(Date.now() - 1000 * 60 * 60 * 3),
    board: ['Q♥', 'J♥', '10♥', '5♣', '8♦'],
  },
  {
    id: 'H004',
    room: '新手桌 #2',
    players: ['張三', 'Lucy Wu'],
    pot: 120000,
    winner: 'Lucy Wu',
    handType: '一對',
    date: new Date(Date.now() - 1000 * 60 * 60 * 6),
    board: ['K♠', '5♣', '2♦', 'J♥', '3♠'],
  },
  {
    id: 'H005',
    room: 'PLO 高額桌',
    players: ['Mike W.', '小美', 'David C.'],
    pot: 800000,
    winner: '小美',
    handType: '同花',
    date: new Date(Date.now() - 1000 * 60 * 60 * 12),
    board: ['9♦', '6♦', '3♦', 'K♠', '2♣'],
  },
]

// Hardcoded example timeline for detail view
const exampleTimeline: TimelineAction[] = [
  {
    stage: 'Preflop',
    actions: [
      '王大明 加注 $20.00',
      'David C. 跟注 $20.00',
      'Sarah L. 棄牌',
    ],
  },
  {
    stage: 'Flop',
    board: ['Q♠', 'J♥', '10♦'],
    actions: [
      '王大明 下注 $40.00',
      'David C. 跟注 $40.00',
    ],
  },
  {
    stage: 'Turn',
    board: ['8♣'],
    actions: [
      '王大明 過牌',
      'David C. 下注 $80.00',
      '王大明 跟注 $80.00',
    ],
  },
  {
    stage: 'River',
    board: ['2♠'],
    actions: [
      '王大明 下注 $200.00',
      'David C. 棄牌',
    ],
  },
  {
    stage: '攤牌',
    actions: [
      '王大明 以 同花順 贏得底池 $9,040.00',
    ],
  },
]

// --- Helpers ---

function CardDisplay({ card, size = 'normal' }: { card: string; size?: 'normal' | 'small' }) {
  const isRed = card.includes('♥') || card.includes('♦')
  const sizeClasses = size === 'small'
    ? 'w-9 h-12 text-sm rounded-md'
    : 'w-11 h-14 text-base rounded-lg'

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center font-bold border',
        sizeClasses,
        isRed
          ? 'border-red-200 bg-red-50 text-[#FF3B30]'
          : 'border-gray-200 bg-gray-50 text-black'
      )}
    >
      {card}
    </span>
  )
}

function StageLabel({ stage }: { stage: string }) {
  const colors: Record<string, string> = {
    Preflop: '#8E8E93',
    Flop: '#007AFF',
    Turn: '#FF9500',
    River: '#AF52DE',
    '攤牌': '#34C759',
  }
  const color = colors[stage] || '#8E8E93'

  return (
    <span
      className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full"
      style={{ color, backgroundColor: `${color}18` }}
    >
      {stage}
    </span>
  )
}

// --- Detail Sheet ---

function HandDetailSheet({
  hand,
  open,
  onClose,
}: {
  hand: HandRecord | null
  open: boolean
  onClose: () => void
}) {
  if (!hand) return null

  // Use example timeline for H001, simplified view for others
  const timeline = hand.id === 'H001' ? exampleTimeline : null

  return (
    <Sheet
      opened={open}
      onBackdropClick={onClose}
      className="pb-safe"
      style={{ height: 'auto', maxHeight: '90vh' }}
    >
      <div className="overflow-y-auto" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5EA]">
          <h3 className="text-lg font-bold text-black">手牌詳情</h3>
          <button onClick={onClose} className="text-sm font-medium text-[#007AFF]">
            關閉
          </button>
        </div>

        {/* Hand Info */}
        <Block className="!mt-2">
          <div className="flex items-center justify-between mb-3">
            <span className="text-base font-bold text-black">{hand.id}</span>
            <span className="text-xs font-semibold text-[#AF52DE] bg-[#AF52DE]/10 px-2 py-0.5 rounded-full">
              {hand.handType}
            </span>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm">
              <DoorOpen className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">房間</span>
              <span className="font-medium text-black ml-auto">{hand.room}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">玩家</span>
              <span className="font-medium text-black ml-auto">
                {hand.players.join(', ')}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Trophy className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">贏家</span>
              <span className="font-semibold text-[#34C759] ml-auto">{hand.winner}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">底池</span>
              <span className="font-semibold text-black ml-auto">
                {formatCurrency(hand.pot)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-[#8E8E93]">時間</span>
              <span className="font-medium text-black ml-auto">
                {formatRelativeTime(hand.date)}
              </span>
            </div>
          </div>
        </Block>

        {/* Board */}
        <BlockTitle>公牌</BlockTitle>
        <Block strong inset>
          <div className="flex gap-1.5 flex-wrap">
            {hand.board.map((card, i) => (
              <CardDisplay key={i} card={card} />
            ))}
          </div>
        </Block>

        {/* Timeline */}
        {timeline ? (
          <>
            <BlockTitle className="flex items-center gap-1.5">
              <FileText className="w-4 h-4" />
              行動時間線
            </BlockTitle>
            <Block strong inset>
              <div className="space-y-4">
                {timeline.map((stage, stageIdx) => (
                  <div key={stageIdx}>
                    {/* Stage Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <StageLabel stage={stage.stage} />
                      {stage.board && (
                        <div className="flex gap-1">
                          {stage.board.map((card, i) => (
                            <CardDisplay key={i} card={card} size="small" />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="space-y-1.5 pl-2 border-l-2 border-[#E5E5EA] ml-1">
                      {stage.actions.map((action, actionIdx) => (
                        <div key={actionIdx} className="flex items-center gap-2 pl-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#8E8E93]" />
                          <p className="text-sm text-black">{action}</p>
                        </div>
                      ))}
                    </div>

                    {/* Divider between stages */}
                    {stageIdx < timeline.length - 1 && (
                      <div className="border-t border-[#E5E5EA] mt-3" />
                    )}
                  </div>
                ))}
              </div>
            </Block>
          </>
        ) : (
          <>
            <BlockTitle>行動時間線</BlockTitle>
            <Block strong inset>
              <div className="flex flex-col items-center py-4 text-center">
                <FileText className="w-8 h-8 text-[#8E8E93] mb-2" />
                <p className="text-sm text-[#8E8E93]">選擇手牌 H001 查看完整行動時間線範例</p>
              </div>
            </Block>
          </>
        )}

        <div className="h-4" />
      </div>
    </Sheet>
  )
}

// --- Main Screen ---

export function AdminHandsScreen() {
  const [search, setSearch] = useState('')
  const [selectedHand, setSelectedHand] = useState<HandRecord | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const filteredHands = useMemo(() => {
    if (!search.trim()) return mockHands

    const q = search.toLowerCase()
    return mockHands.filter(
      (h) =>
        h.id.toLowerCase().includes(q) ||
        h.room.toLowerCase().includes(q) ||
        h.winner.toLowerCase().includes(q) ||
        h.handType.toLowerCase().includes(q) ||
        h.players.some((p) => p.toLowerCase().includes(q))
    )
  }, [search])

  const handleHandClick = (hand: HandRecord) => {
    setSelectedHand(hand)
    setSheetOpen(true)
  }

  const closeSheet = () => {
    setSheetOpen(false)
    setSelectedHand(null)
  }

  return (
    <>
      <Navbar title="手牌記錄" />

      <div className="overflow-y-auto pb-8">
        {/* Searchbar */}
        <Searchbar
          placeholder="搜尋手牌 ID、房間、玩家..."
          value={search}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
        />

        {/* Results Count */}
        <BlockTitle>
          共 {filteredHands.length} 筆記錄
        </BlockTitle>

        {/* Hand List */}
        {filteredHands.length > 0 ? (
          <List strongIos insetIos>
            {filteredHands.map((hand) => (
              <ListItem
                key={hand.id}
                link
                chevronIos
                onClick={() => handleHandClick(hand)}
                title={
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-black">{hand.id}</span>
                    <span className="text-xs font-semibold text-[#AF52DE] bg-[#AF52DE]/10 px-1.5 py-0.5 rounded-full">
                      {hand.handType}
                    </span>
                  </span>
                }
                subtitle={
                  <span className="flex items-center gap-2 text-[#8E8E93]">
                    <span className="flex items-center gap-1">
                      <DoorOpen className="w-3 h-3" />
                      {hand.room}
                    </span>
                    <span>|</span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {hand.players.length} 人
                    </span>
                  </span>
                }
                after={
                  <span className="font-semibold text-black text-sm">
                    {formatCurrency(hand.pot)}
                  </span>
                }
                media={
                  <div className="w-10 h-10 rounded-xl bg-[#AF52DE]/10 flex items-center justify-center">
                    <Spade className="w-5 h-5 text-[#AF52DE]" />
                  </div>
                }
                text={
                  <span className="flex items-center gap-3 text-[#8E8E93]">
                    <span className="flex items-center gap-1">
                      <Trophy className="w-3 h-3 text-[#34C759]" />
                      <span className="text-[#34C759]">{hand.winner}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(hand.date)}
                    </span>
                  </span>
                }
              />
            ))}
          </List>
        ) : (
          <Block className="!mt-8">
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#E5E5EA]/50 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-[#8E8E93]" />
              </div>
              <p className="text-base font-semibold text-[#8E8E93]">找不到手牌記錄</p>
              <p className="text-sm text-[#8E8E93]/60 mt-1">
                請嘗試不同的搜尋條件
              </p>
            </div>
          </Block>
        )}

        {/* Board Preview for latest hand */}
        <BlockTitle>最新公牌</BlockTitle>
        <Block strong inset>
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5">
              {mockHands[0].board.map((card, i) => (
                <CardDisplay key={i} card={card} size="small" />
              ))}
            </div>
            <div className="text-right">
              <p className="text-xs text-[#8E8E93]">{mockHands[0].id}</p>
              <p className="text-xs font-medium text-[#AF52DE]">{mockHands[0].handType}</p>
            </div>
          </div>
        </Block>
      </div>

      {/* Detail Sheet */}
      <HandDetailSheet
        hand={selectedHand}
        open={sheetOpen}
        onClose={closeSheet}
      />
    </>
  )
}
